import { getAccessToken } from '../authentication/supabaseService';

// On-demand report PDFs for the Analysis page. Contract: z_ralph/tasks/1_new_tasks/analysis/api-contract.md

export type AnalysisReportType =
  | 'revenue'
  | 'weekly'
  | 'monthly'
  | 'waste'
  | 'priceAlerts'
  | 'inventoryLeakage'
  | 'inventoryOrder'
  | 'inventoryManagement';

export interface CreateReportJobParams {
  restaurantId: string;
  reportType: AnalysisReportType;
  /** YYYY-MM-DD — week/month types. */
  startDate?: string;
  endDate?: string;
  groupId?: string;
  /** Session uuid — inventoryOrder / inventoryManagement. */
  countSessionId?: string;
  /** Session uuid of the opening count — inventoryLeakage. */
  baselineSessionId?: string;
  /** Any inventory_counts.id of the closing session — inventoryLeakage. */
  endingCountId?: string;
  language: 'en' | 'nl';
}

export interface ReportJobStatus {
  status: 'queued' | 'running' | 'done' | 'failed';
  position: number;
  error?: string;
  warnings?: string[];
  filename?: string;
}

/** The job is gone server-side (10-min TTL or restart) — the user has to request it again. */
export class ReportJobExpiredError extends Error {
  constructor() {
    super('report_job_expired');
    this.name = 'ReportJobExpiredError';
  }
}

/** 429: the user already has the maximum number of downloads in progress. */
export class ReportJobLimitError extends Error {
  constructor() {
    super('report_job_limit');
    this.name = 'ReportJobLimitError';
  }
}

const API_BASE = '/api/analysis/reports/jobs';

async function authHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

/** Surfaces the server's `{ error }` message (400 validation / 403 access) so the UI can show it inline. */
async function throwResponseError(response: Response, fallback: string): Promise<never> {
  const data = await response.json().catch(() => ({}));
  throw new Error(data.error || `${fallback}: ${response.status}`);
}

export async function createReportJob(params: CreateReportJobParams): Promise<{ jobId: string; position: number }> {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(params),
  });
  if (response.status === 429) throw new ReportJobLimitError();
  if (!response.ok) await throwResponseError(response, 'Failed to request report');
  return response.json();
}

export async function getReportJobStatus(jobId: string): Promise<ReportJobStatus> {
  const response = await fetch(`${API_BASE}/${jobId}`, { headers: await authHeaders() });
  if (response.status === 410) throw new ReportJobExpiredError();
  if (!response.ok) await throwResponseError(response, 'Failed to read report status');
  return response.json();
}

export async function downloadReportJobFile(jobId: string): Promise<Blob> {
  const response = await fetch(`${API_BASE}/${jobId}/file`, { headers: await authHeaders() });
  if (response.status === 410) throw new ReportJobExpiredError();
  if (!response.ok) await throwResponseError(response, 'Failed to download report');
  return response.blob();
}
